import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "@/lib/utils";
import {
  CheckCircle, Loader2, CreditCard, Banknote, Smartphone, Receipt, Copy, ExternalLink,
} from "lucide-react";

interface PaymentBooking {
  id: string;
  user_id: string;
  service_name: string;
  service_category?: string;
  booking_date: string;
  booking_time: string;
  duration?: string;
  price: number;
  discount_applied?: number | null;
  deposit_paid_amount?: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: PaymentBooking | null;
  /** When true, dialog will also mark the booking as completed on success. */
  completeAfterPayment?: boolean;
  /** Show the "Mark booking as completed" toggle (defaults true). */
  showCompleteToggle?: boolean;
  /** Optional client display name for the dialog subtitle. */
  clientName?: string | null;
  /** Called after a successful save so the parent can refetch. */
  onSaved?: () => void;
}

const METHOD_META: Record<string, { label: string; icon: any }> = {
  cash: { label: "Cash", icon: Banknote },
  card: { label: "Card Terminal", icon: CreditCard },
  tap_to_pay: { label: "Tap to Pay", icon: Smartphone },
  zelle: { label: "Zelle", icon: Smartphone },
  venmo: { label: "Venmo", icon: Smartphone },
  cashapp: { label: "Cash App", icon: Smartphone },
  other: { label: "Other", icon: Smartphone },
};

/**
 * Reusable "Collect Payment" dialog — mirrors the dialog in Bookings.tsx.
 * Records a manual_payments row + flips the booking to paid (and optionally completed).
 * Loads tip presets, tax rate, and accepted methods from site_settings.
 */
const CollectPaymentDialog = ({
  open,
  onOpenChange,
  booking,
  // Kept for backwards compatibility — payment collection now always completes the booking.
  completeAfterPayment: _completeDefault = true,
  showCompleteToggle: _showCompleteToggle = true,
  clientName,
  onSaved,
}: Props) => {
  const { toast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState<string | null>(null);
  const [tapToPayLoading, setTapToPayLoading] = useState(false);
  // Collecting payment always marks the booking as completed.
  const completeAfterPayment = true;

  const [paySubtotal, setPaySubtotal] = useState<string>("0");
  const [payTip, setPayTip] = useState<string>("0");
  const [payTaxRate, setPayTaxRate] = useState<number>(0);
  const [payNotes, setPayNotes] = useState<string>("");

  const [tipPresets, setTipPresets] = useState<number[]>([15, 20, 25]);
  const [acceptedMethods, setAcceptedMethods] = useState<string[]>([
    "cash", "card", "tap_to_pay", "zelle", "venmo", "cashapp", "other",
  ]);

  // Load payment portal config from site_settings
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key,value")
        .in("key", ["payment_tip_presets", "payment_tax_rate", "payment_accepted_methods"]);
      if (!data) return;
      const get = (k: string) => data.find((r: any) => r.key === k)?.value;

      const tipsRaw = get("payment_tip_presets");
      if (tipsRaw) {
        try {
          const arr = JSON.parse(tipsRaw);
          if (Array.isArray(arr)) setTipPresets(arr.map(Number).filter((n: number) => !isNaN(n)));
        } catch {
          const arr = String(tipsRaw).split(",").map((x) => Number(x.trim())).filter((n) => !isNaN(n));
          if (arr.length) setTipPresets(arr);
        }
      }
      const taxRaw = get("payment_tax_rate");
      if (taxRaw) setPayTaxRate(Number(taxRaw) || 0);
      const methodsRaw = get("payment_accepted_methods");
      if (methodsRaw) {
        try {
          const arr = JSON.parse(methodsRaw);
          if (Array.isArray(arr) && arr.length > 0) setAcceptedMethods(arr);
        } catch {
          const arr = String(methodsRaw).split(",").map((x) => x.trim()).filter(Boolean);
          if (arr.length) setAcceptedMethods(arr);
        }
      }
    })();
  }, []);

  // Reset form when a new booking opens
  useEffect(() => {
    if (open && booking) {
      setPaymentMethod("");
      setStripeCheckoutUrl(null);
      
      const balance = Math.max(
        0,
        Number(booking.price || 0) -
          (Number(booking.discount_applied) || 0) -
          (Number(booking.deposit_paid_amount) || 0)
      );
      setPaySubtotal(balance.toFixed(2));
      setPayTip("0");
      setPayNotes("");
    }
  }, [open, booking]);

  const handleStartTapToPay = async () => {
    if (!booking) return;
    const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
    const tipNum = Math.max(0, Number(payTip) || 0);
    const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
    const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);

    if (totalNum <= 0) {
      toast({ title: "Enter an amount", description: "Subtotal or tip must be greater than $0.", variant: "destructive" });
      return;
    }

    setTapToPayLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("terminal-create-payment-intent", {
        body: {
          booking_id: booking.id,
          user_id: booking.user_id,
          amount: Math.round(totalNum * 100),
          currency: "usd",
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Ready for tap", description: "Hand the device to the client and tap their card." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to start Tap to Pay", variant: "destructive" });
    }
    setTapToPayLoading(false);
  };

  const handlePaymentComplete = async () => {
    if (!booking || !paymentMethod) return;
    setPaymentLoading(true);

    const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
    const tipNum = Math.max(0, Number(payTip) || 0);
    const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
    const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);

    if (paymentMethod === "card" && !stripeCheckoutUrl) {
      try {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: {
            service_name: booking.service_name,
            service_category: booking.service_category,
            service_price: totalNum,
            booking_date: booking.booking_date,
            booking_time: booking.booking_time,
            duration: booking.duration,
            payment_type: "full",
            user_id: booking.user_id,
            booking_id: booking.id,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.url) {
          setStripeCheckoutUrl(data.url);
          window.open(data.url, "_blank");
          toast({
            title: "Card Terminal Ready",
            description: "Checkout opened in new tab. Client can also scan the QR code below. Tap Confirm Payment once paid.",
          });
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (err: any) {
        toast({ title: "Error", description: err.message || "Failed to create card checkout", variant: "destructive" });
      }
      setPaymentLoading(false);
      return;
    }

    // Log manual payment
    const { data: { user: authUser } } = await supabase.auth.getUser();
    await supabase.from("manual_payments").insert({
      booking_id: booking.id,
      user_id: booking.user_id,
      collected_by: authUser?.id,
      method: paymentMethod,
      subtotal: subtotalNum,
      tip_amount: tipNum,
      tax_amount: taxNum,
      total_amount: totalNum,
      notes: payNotes,
    });

    // Update booking
    const updateData: any = {
      payment_status: "paid",
      payment_type: paymentMethod,
      tip_amount: tipNum,
      tax_amount: taxNum,
    };
    if (completeAfterPayment) updateData.status = "completed";

    const { error } = await supabase.from("bookings").update(updateData).eq("id", booking.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({
        title: completeAfterPayment ? "Booking Completed" : "Payment Collected",
        description: `$${totalNum.toFixed(2)} via ${paymentMethod}`,
      });

      // Auto-send receipt (best effort)
      supabase.functions.invoke("send-payment-receipt", {
        body: {
          user_id: booking.user_id,
          booking_id: booking.id,
          service_name: booking.service_name,
          booking_date: booking.booking_date,
          booking_time: booking.booking_time,
          method: paymentMethod,
          subtotal: subtotalNum,
          tip_amount: tipNum,
          tax_amount: taxNum,
          total_amount: totalNum,
          notes: payNotes,
        },
      }).catch((e) => console.error("Receipt send failed:", e));

      onSaved?.();
    }

    setPaymentLoading(false);
    onOpenChange(false);
  };

  if (!booking) return null;

  const subtotalNum = Math.max(0, Number(paySubtotal) || 0);
  const tipNum = Math.max(0, Number(payTip) || 0);
  const taxNum = +(subtotalNum * (payTaxRate / 100)).toFixed(2);
  const totalNum = +(subtotalNum + tipNum + taxNum).toFixed(2);
  const baseBalance = Math.max(
    0,
    Number(booking.price || 0) -
      (Number(booking.discount_applied) || 0) -
      (Number(booking.deposit_paid_amount) || 0)
  );

  const methods = acceptedMethods
    .filter((m) => METHOD_META[m])
    .map((m) => ({ value: m, ...METHOD_META[m] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border/50 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
            <Receipt size={20} className="text-primary" />
            {completeAfterPayment ? "Complete & Collect Payment" : "Collect Payment"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {booking.service_name}{clientName ? ` — ${clientName}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Price Summary */}
          <div className="border border-border/50 rounded-md p-3 space-y-1.5 text-xs bg-background/50">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Service Price</span>
              <span>${Number(booking.price).toFixed(2)}</span>
            </div>
            {Number(booking.discount_applied) > 0 && (
              <div className="flex justify-between text-primary">
                <span>Discount</span>
                <span>-${Number(booking.discount_applied).toFixed(2)}</span>
              </div>
            )}
            {Number(booking.deposit_paid_amount) > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Deposit Paid</span>
                <span>-${Number(booking.deposit_paid_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t border-border pt-1.5 text-sm">
              <span>Suggested Balance</span>
              <span>${baseBalance.toFixed(2)}</span>
            </div>
          </div>

          {/* Editable subtotal */}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
              Amount to Collect
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paySubtotal}
                onChange={(e) => setPaySubtotal(e.target.value)}
                className="pl-7 bg-background border-border/50"
              />
            </div>
          </div>

          {/* Tip presets */}
          {tipPresets.length > 0 && (
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">Tip</label>
              <div className="flex gap-1.5 flex-wrap mb-2">
                <button
                  type="button"
                  onClick={() => setPayTip("0")}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-md border transition-all",
                    Number(payTip) === 0
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/50 text-muted-foreground hover:border-primary/40"
                  )}
                >
                  None
                </button>
                {tipPresets.map((pct) => {
                  const tipVal = +(subtotalNum * (pct / 100)).toFixed(2);
                  const isActive = Math.abs(Number(payTip) - tipVal) < 0.005 && tipVal > 0;
                  return (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setPayTip(tipVal.toFixed(2))}
                      className={cn(
                        "px-3 py-1.5 text-xs rounded-md border transition-all",
                        isActive
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/50 text-muted-foreground hover:border-primary/40"
                      )}
                    >
                      {pct}% (${tipVal.toFixed(2)})
                    </button>
                  );
                })}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payTip}
                  onChange={(e) => setPayTip(e.target.value)}
                  placeholder="Custom tip"
                  className="pl-7 bg-background border-border/50"
                />
              </div>
            </div>
          )}

          {/* Receipt summary */}
          <div className="border border-primary/30 bg-primary/5 rounded-md p-3 space-y-1 text-xs">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotalNum.toFixed(2)}</span></div>
            {tipNum > 0 && (
              <div className="flex justify-between"><span>Tip</span><span>${tipNum.toFixed(2)}</span></div>
            )}
            {payTaxRate > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Tax ({payTaxRate}%)</span><span>${taxNum.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base border-t border-primary/30 pt-1.5 text-primary">
              <span>Total</span><span>${totalNum.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Payment Method</p>
            <div className="grid grid-cols-3 gap-2">
              {methods.map((method) => (
                <button
                  key={method.value}
                  onClick={() => setPaymentMethod(method.value)}
                  className={cn(
                    "flex flex-col items-center gap-1 p-2.5 rounded-md border transition-all text-[11px]",
                    paymentMethod === method.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  <method.icon size={16} />
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Card Terminal: Stripe QR + Copy link */}
          {paymentMethod === "card" && stripeCheckoutUrl && (
            <div className="border border-primary/40 bg-primary/5 rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary">
                <CreditCard size={14} />
                Stripe Checkout Ready
              </div>
              <div className="flex justify-center bg-background p-3 rounded-md">
                <QRCodeSVG value={stripeCheckoutUrl} size={160} level="M" />
              </div>
              <p className="text-[11px] text-muted-foreground text-center leading-snug">
                Have the client scan this QR code to pay on their phone, or share the link below.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-[11px] gap-1"
                  onClick={() => {
                    navigator.clipboard.writeText(stripeCheckoutUrl);
                    toast({ title: "Copied", description: "Stripe link copied to clipboard." });
                  }}
                >
                  <Copy size={12} /> Copy Link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-[11px] gap-1"
                  onClick={() => window.open(stripeCheckoutUrl, "_blank")}
                >
                  <ExternalLink size={12} /> Open
                </Button>
              </div>
            </div>
          )}

          {/* Tap to Pay */}
          {paymentMethod === "tap_to_pay" && (
            <div className="border border-primary/40 bg-primary/5 rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary">
                <Smartphone size={14} />
                Tap to Pay (Stripe Terminal)
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Starts a card-present PaymentIntent on Stripe. The native app's Stripe Terminal SDK
                will pick it up to collect the client's NFC tap.
              </p>
              <Button
                type="button"
                size="sm"
                className="w-full text-[11px] gap-1"
                onClick={handleStartTapToPay}
                disabled={tapToPayLoading || !(Number(paySubtotal) > 0 || Number(payTip) > 0)}
              >
                {tapToPayLoading ? <Loader2 size={12} className="animate-spin" /> : <Smartphone size={12} />}
                Start Tap to Pay
              </Button>
            </div>
          )}

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
              Notes <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
            </label>
            <Input
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              placeholder="e.g. Zelle confirmation #, last 4 of card…"
              className="bg-background border-border/50 text-sm"
            />
          </div>

          {/* Collecting payment always marks the booking completed */}
          <div className="flex items-center gap-2 p-3 border border-emerald-500/30 rounded-md bg-emerald-500/5">
            <CheckCircle size={14} className="text-emerald-600 shrink-0" />
            <p className="text-[11px] text-muted-foreground">
              This booking will be marked as <span className="text-emerald-600 font-medium">Completed</span> once payment is collected.
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-xs tracking-widest uppercase text-muted-foreground"
          >
            Cancel
          </Button>
          <Button
            disabled={paymentLoading || !paymentMethod || !(Number(paySubtotal) > 0 || Number(payTip) > 0)}
            onClick={() => handlePaymentComplete()}
            className="text-xs tracking-widest uppercase gap-1"
          >
            {paymentLoading ? <Loader2 size={14} className="animate-spin" /> : (
              <>
                <CheckCircle size={14} />
                {paymentMethod === "card"
                  ? (stripeCheckoutUrl ? "Confirm Payment" : "Generate Card Checkout")
                  : completeAfterPayment ? "Complete & Collect" : "Collect Payment"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CollectPaymentDialog;
